import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import { lightSkeletonTheme, darkSkeletonTheme } from "./skeletonTheme";

// Mirrors Home.jsx's section-by-section layout (hero / featured drop /
// value strip / story band) so the page doesn't jump around once the real
// content swaps in.
const HomeSkeleton = ({ navHeight }) => {
  return (
    <div style={{ minHeight: navHeight ? `calc(100vh - ${navHeight}px)` : undefined }}>
      {/* HERO */}
      <section
        className="relative overflow-hidden bg-primary-dark px-6 pt-14 pb-24 md:px-12 md:pt-20 md:pb-36"
        style={{ clipPath: "polygon(0 0, 100% 0, 100% 90%, 50% 100%, 0 90%)" }}
      >
        <SkeletonTheme {...darkSkeletonTheme}>
          <div className="relative grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 lg:col-span-7 text-center lg:text-left">
              <Skeleton height={56} className="max-w-xl" containerClassName="block mb-2" />
              <Skeleton height={56} className="max-w-md" containerClassName="block mb-4" />
              <Skeleton height={20} width="70%" containerClassName="block mb-2" />
              <Skeleton height={16} width="45%" containerClassName="block mb-6" />
              <Skeleton height={48} width={160} borderRadius={999} />
            </div>
            <div className="col-span-12 lg:col-span-5 flex justify-center">
              <div className="w-3/4 sm:w-3/5 lg:w-full max-w-xs">
                <Skeleton className="aspect-[4/5]" borderRadius={12} />
              </div>
            </div>
          </div>
        </SkeletonTheme>
      </section>

      {/* FEATURED DROP */}
      <section className="outer-container container px-3 py-16">
        <SkeletonTheme {...lightSkeletonTheme}>
          <div className="mb-8 text-left">
            <Skeleton width={140} height={14} containerClassName="block mb-2" />
            <Skeleton width={320} height={34} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 md:grid-rows-2 gap-5">
            {Array.from({ length: 5 }).map((_, idx) => (
              <div
                key={idx}
                className={`flex flex-col h-full rounded-xl overflow-hidden ${idx === 0 ? "col-span-2 row-span-2" : "col-span-1"}`}
              >
                <Skeleton className={idx === 0 ? "flex-1 aspect-square" : "aspect-square"} />
                <div className="p-3">
                  <Skeleton width="70%" height={14} containerClassName="block mb-1" />
                  <Skeleton width="35%" height={14} />
                </div>
              </div>
            ))}
          </div>
        </SkeletonTheme>
      </section>

      {/* VALUE STRIP */}
      <section className="bg-primary-dark py-10 px-6">
        <SkeletonTheme {...darkSkeletonTheme}>
          <div className="outer-container container grid grid-cols-1 sm:grid-cols-3 gap-8 text-center max-w-3xl mx-auto">
            {Array.from({ length: 3 }).map((_, idx) => (
              <div key={idx}>
                <Skeleton circle width={44} height={44} containerClassName="block mx-auto mb-3" />
                <Skeleton width="60%" height={18} containerClassName="block mx-auto mb-1" />
                <Skeleton width="80%" height={14} containerClassName="block mx-auto" />
              </div>
            ))}
          </div>
        </SkeletonTheme>
      </section>

      {/* STORY BAND */}
      <section className="bg-gradient-to-br from-primary-dark to-primary-medium/80 px-6 py-14 md:px-12">
        <SkeletonTheme {...darkSkeletonTheme}>
          <div className="grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 lg:col-span-7 text-left">
              <Skeleton width={110} height={14} containerClassName="block mb-3" />
              <Skeleton height={40} containerClassName="block mb-2" />
              <Skeleton height={40} width="80%" containerClassName="block mb-5" />
              <Skeleton count={3} height={16} containerClassName="block mb-2" />
              <Skeleton height={48} width={180} borderRadius={999} containerClassName="block mt-4" />
            </div>
            <div className="col-span-12 lg:col-span-5 flex justify-center">
              <div className="w-56 sm:w-64 lg:w-72">
                <Skeleton className="aspect-[4/5]" borderRadius={12} />
              </div>
            </div>
          </div>
        </SkeletonTheme>
      </section>
    </div>
  );
};

export default HomeSkeleton;
